import React, { useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import EntoRidHome from './pages/EntoRidHome';
import AdminLeads from './pages/AdminLeads';
import ServicesIndex from './pages/ServicesIndex';
import ServiceDetail from './pages/ServiceDetail';
import PricingPage from './pages/PricingPage';
import ProductsPage from './pages/ProductsPage';
import SpecialOffers from './pages/SpecialOffers';
import GalleryImages from './pages/GalleryImages';
import GalleryVideos from './pages/GalleryVideos';
import TestimonialsReviews from './pages/TestimonialsReviews';
import TestimonialsVideos from './pages/TestimonialsVideos';
import AboutMe from './pages/AboutMe';
import Contact from './pages/Contact';
import { PrivacyPolicy, TermsConditions, RefundPolicy } from './pages/LegalPages';
import RealTimeCalculator from './components/RealTimeCalculator';
import FloatingContact from './components/FloatingContact';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import { PanelAuthProvider } from './pages/official/PanelAuthContext';
import OfficialGateway from './pages/official/OfficialGateway';
import PortalSelection from './pages/official/PortalSelection';
import { AdminLogin, EmployeeLogin } from './pages/official/PanelLogin';
import AdminPortal from './pages/official/AdminPortal';
import EmployeePortal from './pages/official/EmployeePortal';
import PageviewTracker from './components/PageviewTracker';
import { CustomerAuthProvider, useCustomerAuth } from './contexts/CustomerAuthContext';
import CustomerAuthModal from './components/CustomerAuthModal';
import CustomerAccount from './pages/CustomerAccount';
import GoogleAuthCallback from './components/GoogleAuthCallback';
import CompleteMobileModal from './components/CompleteMobileModal';
import Phase0Check from './pages/Phase0Check';
import BlogIndex from './pages/BlogIndex';
import BlogArticle from './pages/BlogArticle';
import PageTransition from './components/PageTransition';
import ScrollToTop from './components/ScrollToTop';
import { SeasonalThemeProvider } from './contexts/SeasonalThemeContext';

// Shared calculator state at the root so every page can open it
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PanelAuthProvider>
        <CustomerAuthProvider>
        <SeasonalThemeProvider>
        <AppShell />
        </SeasonalThemeProvider>
        </CustomerAuthProvider>
        </PanelAuthProvider>
      </BrowserRouter>
      <Toaster />
      <SonnerToaster position="bottom-right" richColors closeButton />
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  const [preselectedPest, setPreselectedPest] = useState(null);
  const [lockedSegment, setLockedSegment] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState('signup');
  const [authModalTitle, setAuthModalTitle] = useState(null);
  const [authModalSubtitle, setAuthModalSubtitle] = useState(null);
  const [completeMobileOpen, setCompleteMobileOpen] = useState(false);
  const { isLoggedIn, track, customer } = useCustomerAuth();

  // Prompt for mobile once, after login, if the account has no mobile (Google-only signups).
  React.useEffect(() => {
    if (isLoggedIn && customer && !customer.mobile) {
      setCompleteMobileOpen(true);
    } else {
      setCompleteMobileOpen(false);
    }
  }, [isLoggedIn, customer]);

  // Gate the calculator behind customer auth — anonymous users are nudged to sign up first.
  const openCalc = (pestKey, segment) => {
    const preset = typeof pestKey === 'string' ? pestKey : null;
    const seg = (segment === 'commercial' || segment === 'industrial') ? segment : null;
    if (!isLoggedIn) {
      // Stash the pest intent so we open the calculator post-signup
      try {
        sessionStorage.setItem('entorid_pending_calc_pest', preset || '');
        sessionStorage.setItem('entorid_pending_calc_segment', seg || '');
      } catch (e) { /* ignore */ }
      setAuthModalTitle('Sign up to see your price');
      setAuthModalSubtitle('Takes 20 seconds · we’ll send your quote by email too');
      setAuthModalView('signup');
      setAuthModalOpen(true);
      track('calculator_open_gated', { pest: preset, source: 'header_or_cta', segment: seg });
      return;
    }
    setPreselectedPest(preset);
    setLockedSegment(seg);
    setIsCalcOpen(true);
    track('calculator_open', { pest: preset, segment: seg });
  };
  const closeCalc = () => {
    setIsCalcOpen(false);
    setTimeout(() => {
      setPreselectedPest(null);
      setLockedSegment(null);
    }, 300);
  };

  const openAuthModal = (view = 'login') => {
    setAuthModalTitle(null);
    setAuthModalSubtitle(null);
    setAuthModalView(view);
    setAuthModalOpen(true);
  };

  // When auth modal closes after successful login/signup, if a pending pest exists, open calc
  const handleAuthClose = () => {
    setAuthModalOpen(false);
    try {
      const pending = sessionStorage.getItem('entorid_pending_calc_pest');
      const pendingSeg = sessionStorage.getItem('entorid_pending_calc_segment');
      if (pending !== null && isLoggedIn) {
        sessionStorage.removeItem('entorid_pending_calc_pest');
        sessionStorage.removeItem('entorid_pending_calc_segment');
        setPreselectedPest(pending || null);
        setLockedSegment((pendingSeg === 'commercial' || pendingSeg === 'industrial') ? pendingSeg : null);
        setIsCalcOpen(true);
      }
    } catch (e) { /* ignore */ }
  };

  // Watch isLoggedIn to auto-launch calc after successful signup
  React.useEffect(() => {
    if (isLoggedIn && authModalOpen) {
      // Successful auth — close modal which will trigger handleAuthClose -> opens calc if pending
      setAuthModalOpen(false);
      try {
        const pending = sessionStorage.getItem('entorid_pending_calc_pest');
        if (pending !== null) {
          sessionStorage.removeItem('entorid_pending_calc_pest');
          setPreselectedPest(pending || null);
          setIsCalcOpen(true);
        }
      } catch (e) { /* ignore */ }
    }
  }, [isLoggedIn, authModalOpen]);

  // Google-Auth callback short-circuit: if we're returning from
  // https://auth.emergentagent.com with a `#session_id=` fragment, process
  // it BEFORE any other route can render. Uses useLocation().hash so it
  // stays reactive.
  if (location.hash?.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }

  return (
    <>
      <PageviewTracker />
      <ScrollToTop />
      <PageTransition>
      <Routes>
        <Route path="/" element={<EntoRidHome onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/services" element={<ServicesIndex onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/services/:slug" element={<ServiceDetail onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/pricing/:slug" element={<PricingPage onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/products" element={<ProductsPage onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/special-offers" element={<SpecialOffers onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/gallery" element={<Navigate to="/gallery/images" replace />} />
        <Route path="/gallery/images" element={<GalleryImages onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/gallery/videos" element={<GalleryVideos onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/testimonials" element={<TestimonialsReviews onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/testimonials/reviews" element={<TestimonialsReviews onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/testimonials/videos" element={<TestimonialsVideos onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/blog" element={<BlogIndex onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/blog/:slug" element={<BlogArticle onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/about" element={<AboutMe onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/about-us" element={<AboutMe onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/about-me" element={<AboutMe onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/contact" element={<Contact onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/contact-us" element={<Contact onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/reviews" element={<TestimonialsReviews onOpenCalculator={openCalc} onOpenAuth={openAuthModal} />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy onOpenCalculator={openCalc} />} />
        <Route path="/terms-conditions" element={<TermsConditions onOpenCalculator={openCalc} />} />
        <Route path="/refund-policy" element={<RefundPolicy onOpenCalculator={openCalc} />} />
        <Route path="/account" element={<CustomerAccount onOpenAuth={openAuthModal} />} />
        <Route path="/admin" element={<AdminLeads />} />
        <Route path="/official" element={<OfficialGateway />} />
        <Route path="/official/portal-selection" element={<PortalSelection />} />
        <Route path="/official/admin-login" element={<AdminLogin />} />
        <Route path="/official/employee-login" element={<EmployeeLogin />} />
        <Route path="/official/admin" element={<AdminPortal />} />
        <Route path="/official/employee" element={<EmployeePortal />} />
        <Route path="/phase0" element={<Phase0Check />} />
      </Routes>
      </PageTransition>
      <RealTimeCalculator isOpen={isCalcOpen} onClose={closeCalc} preselectedPest={preselectedPest} lockedSegment={lockedSegment} />
      <FloatingContact onOpenCalculator={openCalc} />
      <CustomerAuthModal
        open={authModalOpen}
        onClose={handleAuthClose}
        defaultView={authModalView}
        title={authModalTitle}
        subtitle={authModalSubtitle}
      />
      <CompleteMobileModal
        open={completeMobileOpen}
        onClose={() => setCompleteMobileOpen(false)}
      />
    </>
  );
}

export default App;
